// src/protocols/konto.js — експортът на ВОП протоколите към Конто 6 и
// настройките му (ПРОМТ-18). Два диалога, отваряни от менюто „Действия" на
// екрана с протоколите:
//  • „Експорт към Конто 6" — избор на месец, преглед какво ще влезе във файла,
//    сваляне, история на направените файлове („Свали пак" дава байт-същия).
//  • „Настройки за Конто" — кодовете на фирмата (папка, вид документ, сметки,
//    видове сделки, текст, вальор) и кодовете на доставчиците ѝ.
// Самият файл се строи на сървъра (services/kontoVop.js) — тук е само екранът.

import { el, clear, $, field, input, row } from '../shared/dom.js';
import { money, dfmt } from '../shared/format.js';
import { toast } from '../shared/toast.js';
import { api } from '../shared/api.js';
import { openModal, closeModal } from '../shared/modal.js';
import { state, cq } from '../shared/state.js';
import { supplierKey } from '../../services/kontoVop.js';

const MONTHS = ['', 'януари', 'февруари', 'март', 'април', 'май', 'юни', 'юли', 'август', 'септември', 'октомври', 'ноември', 'декември'];
function monthLabel(ym) { const m = /^(\d{4})-(\d{2})$/.exec(ym || ''); return m ? MONTHS[+m[2]] + ' ' + m[1] : 'всички месеци'; }

// Експортът е само за кантората — сървърът отказва същото (routes/protocols.js).
export function kontoAllowed() { const u = state().user; return !!u && u.role !== 'client'; }

function alertBox(color, text) {
  return el('div', { class: 'alert ' + color, role: color === 'red' ? 'alert' : 'status', style: 'margin-top:10px' }, el('div', { text }));
}

async function downloadRun(runId, filename) {
  const res = await fetch('/api/protocols/konto-export/runs/' + runId + '/file', { credentials: 'same-origin' });
  if (!res.ok) { toast('Файлът не можа да се свали.', true); return; }
  const blob = await res.blob();
  const a = el('a', { href: URL.createObjectURL(blob), download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// ---- Експорт ---------------------------------------------------------------

export function exportDialog(protocols) {
  if (!state().activeCompany) { toast('Първо изберете фирма.', true); return; }
  const list = Array.isArray(protocols) ? protocols : [];
  const months = [...new Set(list.map((p) => String(p.protocol_date || '').slice(0, 7)).filter((m) => /^\d{4}-\d{2}$/.test(m)))].sort().reverse();
  // По подразбиране — най-новият месец, в който има още неизнесени протоколи.
  const firstOpen = months.find((m) => list.some((p) => String(p.protocol_date || '').slice(0, 7) === m && !p.konto_exported_at)) || months[0] || '';

  const monthSel = el('select', {}, el('option', { value: '', text: 'Всички месеци' }), ...months.map((m) => el('option', { value: m, text: monthLabel(m) })));
  monthSel.value = firstOpen;
  const inclBox = el('input', { type: 'checkbox' });
  const incl = el('label', { style: 'display:flex;align-items:center;gap:8px;font-size:var(--fs-md)' }, inclBox, el('span', { text: 'Включи и вече изнесените протоколи (повторен файл)' }));
  const preview = el('div', {});
  const histBox = el('div', {});
  const dlBtn = el('button', { class: 'btn', text: 'Свали файла за Конто', disabled: true, onClick: doExport });

  async function load() {
    clear(preview);
    preview.appendChild(el('p', { class: 'muted', text: 'Преглеждам…' }));
    dlBtn.disabled = true;
    let r;
    try {
      r = await api('/protocols/konto-export/preview', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, month: monthSel.value, include_exported: inclBox.checked }) });
    } catch (e) { clear(preview); preview.appendChild(alertBox('red', e.message)); return; }
    renderPreview(r);
  }

  function renderPreview(r) {
    clear(preview);
    const stops = r.stops || [];
    preview.appendChild(el('div', { style: 'margin:10px 0;padding:12px;border:1px solid var(--border);border-radius:8px' },
      el('div', { style: 'font-size:20px;font-weight:600', text: r.count + (r.count === 1 ? ' протокол' : ' протокола') + ' · ' + monthLabel(r.month) }),
      el('div', { class: 'muted', text: 'Данъчна основа ' + money(r.total_base, 'EUR') + ' · ДДС ' + money(r.total_vat, 'EUR') + (r.already_exported ? ' · вече изнесени за периода: ' + r.already_exported : '') })));
    if (!r.matched) { preview.appendChild(el('p', { class: 'muted', text: 'За този период няма протоколи.' })); return; }
    if (!r.count && !stops.length) preview.appendChild(el('p', { class: 'muted', text: 'Всичко за периода вече е изнесено. Отметнете „Включи и вече изнесените", ако ви трябва файлът пак.' }));
    for (const s of stops) preview.appendChild(alertBox('red', s));
    if (stops.length) preview.appendChild(el('p', { class: 'muted', text: 'Файл не се прави, докато горното не се оправи — Конто би го отказал или би осчетоводил грешно. Кодовете на доставчиците са в „Действия → Настройки за Конто".' }));
    for (const w of r.warnings || []) preview.appendChild(alertBox('amber', w));
    for (const s of r.skipped || []) preview.appendChild(alertBox('blue', 'Протокол № ' + s.number + ' не влиза: ' + s.reason));
    if (r.items && r.items.length) {
      const tb = el('tbody');
      r.items.forEach((it) => tb.appendChild(el('tr', {},
        el('td', { style: 'font-weight:600', text: it.number }),
        el('td', { text: dfmt(it.protocol_date) }),
        el('td', {}, el('div', { text: it.supplier_name || '' }), el('div', { class: 'muted', style: 'font-size:12px', text: 'ф-ра ' + it.invoice_no + (it.partner_mode === 'code' ? ' · по код' : ' · по Булстат/ДДС №') })),
        el('td', { class: 'num', text: money(it.tax_base, 'EUR') }),
        el('td', { class: 'num', text: money(it.vat_amount, 'EUR') }),
        el('td', { class: 'hide-sm', style: 'font-size:12px' }, el('div', { text: 'Дт ' + it.dt }), el('div', { text: 'Кт ' + it.kt }))
      )));
      preview.appendChild(el('div', { style: 'overflow:auto;margin-top:10px' }, el('table', {}, el('thead', {}, el('tr', {},
        el('th', { scope: 'col', text: '№' }), el('th', { scope: 'col', text: 'Дата' }), el('th', { scope: 'col', text: 'Доставчик' }),
        el('th', { scope: 'col', class: 'num', text: 'Основа' }), el('th', { scope: 'col', class: 'num', text: 'ДДС' }), el('th', { scope: 'col', class: 'hide-sm', text: 'Статия' }))), tb)));
      preview.appendChild(el('p', { class: 'muted', style: 'margin-top:8px', text: 'Вальор: ' + (r.codes.valeur === 'doc' ? 'датата на протокола' : 'последният ден на месеца') + ' · папка ' + r.codes.folder + ' · вид документ ' + r.codes.doc_type + '. В Конто: Администриране → Връзка с други системи → Импорт на записвания.' }));
    }
    dlBtn.disabled = !(r.count && !stops.length);
  }

  async function doExport() {
    dlBtn.disabled = true;
    try {
      const r = await api('/protocols/konto-export', { method: 'POST', body: JSON.stringify({ company_id: state().activeCompany, month: monthSel.value, include_exported: inclBox.checked }) });
      await downloadRun(r.run_id, r.filename);
      toast('Файлът ' + r.filename + ' е готов — ' + r.count + ' протокола са отметнати като изнесени.');
      await Promise.all([load(), loadHistory()]);
      if (state().view === 'protocols') window.CRM.renderView($('#page'));
    } catch (e) { toast(e.message, true); dlBtn.disabled = false; }
  }

  async function loadHistory() {
    clear(histBox);
    let runs = [];
    try { runs = await api('/protocols/konto-export/runs' + cq()); } catch (e) { histBox.appendChild(alertBox('red', e.message)); return; }
    histBox.appendChild(el('h4', { text: 'Направени файлове', style: 'margin:16px 0 6px' }));
    if (!runs.length) { histBox.appendChild(el('p', { class: 'muted', text: 'Още няма направени файлове за тази фирма.' })); return; }
    const tb = el('tbody');
    runs.forEach((r) => tb.appendChild(el('tr', {},
      el('td', { text: new Date(r.created_at).toLocaleString('bg-BG', { timeZone: 'Europe/Sofia' }) }),
      el('td', { text: monthLabel(r.month) }),
      el('td', { class: 'num', text: String(r.count) }),
      el('td', { class: 'num hide-sm', text: money(r.total_vat, 'EUR') }),
      el('td', { class: 'hide-sm', text: r.created_by_name || '' }),
      el('td', {}, el('button', { class: 'btn sm ghost', text: 'Свали пак', 'aria-label': 'Свали пак ' + r.filename, onClick: () => downloadRun(r.id, r.filename) }))
    )));
    histBox.appendChild(el('div', { style: 'overflow:auto' }, el('table', {}, el('thead', {}, el('tr', {},
      el('th', { scope: 'col', text: 'Кога' }), el('th', { scope: 'col', text: 'Период' }), el('th', { scope: 'col', class: 'num', text: 'Бр.' }),
      el('th', { scope: 'col', class: 'num hide-sm', text: 'ДДС' }), el('th', { scope: 'col', class: 'hide-sm', text: 'От кого' }), el('th', { scope: 'col', text: 'Файл' }))), tb)));
  }

  monthSel.addEventListener('change', load);
  inclBox.addEventListener('change', load);

  const body = el('div', {},
    el('p', { class: 'muted', text: 'Файлът е за „Импорт на записвания" в Конто 6: по една статия Дт 4531 / Кт 4532 на протокол. Изнесените протоколи се отмятат, за да не влязат втори път.' }),
    row(field('Месец', monthSel), el('div', { class: 'field' }, incl)),
    preview, histBox);
  const foot = el('div', {}, el('button', { class: 'btn ghost', text: 'Затвори', onClick: closeModal }), dlBtn);
  openModal({ title: 'Експорт към Конто 6', body, foot, wide: true });
  load(); loadHistory();
}

// ---- Настройки -------------------------------------------------------------

const CODE_FIELDS = [
  ['folder', 'Папка в Конто'],
  ['doc_type', 'Вид документ'],
  ['acc_dt', 'Сметка дебит (ДДС покупки)'],
  ['acc_kt', 'Сметка кредит (ДДС продажби)'],
  ['deal_dt', 'Вид сделка на дебитния ред'],
  ['deal_kt', 'Вид сделка на кредитния ред'],
];

export async function settingsDialog(protocols) {
  if (!state().activeCompany) { toast('Първо изберете фирма.', true); return; }
  let s;
  try { s = await api('/protocols/konto-settings' + cq()); } catch (e) { toast(e.message, true); return; }
  const defs = s.defaults || {};
  const codes = s.codes || {};
  const f = {};
  for (const [k] of CODE_FIELDS) f[k] = input({ value: codes[k] || '', placeholder: defs[k] || '' });
  f.text = input({ value: codes.text || '', placeholder: 'празно = предметът на доставката' });
  f.valeur = el('select', {}, el('option', { value: 'eom', text: 'последният ден на месеца' }), el('option', { value: 'doc', text: 'датата на протокола' }));
  f.valeur.value = codes.valeur === 'doc' ? 'doc' : 'eom';

  // Доставчиците: всички от протоколите на фирмата + записаните в настройките.
  const known = Object.assign({}, s.suppliers || {});
  const byKey = {};
  (Array.isArray(protocols) ? protocols : []).forEach((p) => { const k = supplierKey(p); if (k && !byKey[k]) byKey[k] = { name: p.supplier_name || '', vat: p.supplier_vat || '' }; });
  for (const k of Object.keys(known)) if (!byKey[k]) byKey[k] = { name: known[k].name || k, vat: /^[A-Z]{2}/.test(k) ? k : '' };
  const keys = Object.keys(byKey).sort((a, b) => byKey[a].name.localeCompare(byKey[b].name, 'bg'));
  const supInputs = {};
  const tb = el('tbody');
  keys.forEach((k) => {
    const cur = known[k] || {};
    const mk = (val, label, ph) => input({ value: val || '', placeholder: ph || '', 'aria-label': label + ' — ' + byKey[k].name });
    supInputs[k] = {
      partner_id: mk(cur.partner_id, 'Булстат в Конто', byKey[k].vat),
      supplier_code: mk(cur.supplier_code, 'Код доставчик'),
      customer_code: mk(cur.customer_code, 'Код клиент'),
    };
    tb.appendChild(el('tr', {},
      el('td', {}, el('div', { style: 'font-weight:600', text: byKey[k].name }), el('div', { class: 'muted', style: 'font-size:12px', text: byKey[k].vat || 'без ДДС №' })),
      el('td', {}, supInputs[k].partner_id), el('td', {}, supInputs[k].supplier_code), el('td', {}, supInputs[k].customer_code)));
  });

  const body = el('div', {},
    el('p', { class: 'muted', text: 'Попълвате само това, което в Конто на тази фирма е различно от обичайното. Празно поле = стойността в сиво.' }),
    el('h4', { text: 'Статията', style: 'margin:10px 0 6px' }),
    row(field(CODE_FIELDS[0][1], f.folder), field(CODE_FIELDS[1][1], f.doc_type)),
    row(field(CODE_FIELDS[2][1], f.acc_dt), field(CODE_FIELDS[3][1], f.acc_kt)),
    row(field(CODE_FIELDS[4][1], f.deal_dt), field(CODE_FIELDS[5][1], f.deal_kt)),
    row(field('Текст на статията', f.text), field('Вальор (периодът в Конто)', f.valeur)),
    el('h4', { text: 'Доставчици', style: 'margin:16px 0 6px' }),
    el('p', { class: 'muted', text: 'Как Конто да разпознае доставчика. Най-сигурно: напишете кодовете му в Конто (доставчик и клиент, напр. 014 и 003) — тогава Конто не създава нищо ново. Ако ги оставите празни, Конто го търси по ДДС номера и ако не го намери, създава нов контрагент — с код, по-дълъг от вашите. Ако доставчикът вече е в Конто с друг Булстат, напишете го в „Булстат в Конто".' }),
    keys.length
      ? el('div', { style: 'overflow:auto' }, el('table', { class: 'zaplati-side-table' }, el('thead', {}, el('tr', {},
        el('th', { scope: 'col', text: 'Доставчик' }), el('th', { scope: 'col', text: 'Булстат в Конто' }), el('th', { scope: 'col', text: 'Код доставчик' }), el('th', { scope: 'col', text: 'Код клиент' }))), tb))
      : el('p', { class: 'muted', text: 'Още няма протоколи — доставчиците ще се появят тук след първия.' }));

  async function save() {
    const outCodes = {};
    for (const k of Object.keys(f)) outCodes[k] = f[k].value;
    const suppliers = {};
    for (const k of keys) suppliers[k] = { name: byKey[k].name, partner_id: supInputs[k].partner_id.value, supplier_code: supInputs[k].supplier_code.value, customer_code: supInputs[k].customer_code.value };
    try {
      await api('/protocols/konto-settings' + cq(), { method: 'PUT', body: JSON.stringify({ codes: outCodes, suppliers }) });
      toast('Настройките за Конто са записани.'); closeModal();
    } catch (e) { toast(e.message, true); }
  }
  const foot = el('div', {}, el('button', { class: 'btn ghost', text: 'Отказ', onClick: closeModal }), el('button', { class: 'btn', text: 'Запази', onClick: save }));
  openModal({ title: 'Настройки за Конто 6 — протоколи ВОП', body, foot, wide: true });
}
